import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { backofficeLinks } from "@/components/lib/SidebarLinks";
import { useState, useMemo, useEffect } from "react";
import Stepper from "@/components/common/stepper";
import { Form } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import Step1 from "@/components/menu-management/fish-chips/add-fish-chips/step1";
import Step2 from "@/components/menu-management/fish-chips/add-fish-chips/step2";
import Step3 from "@/components/menu-management/fish-chips/add-fish-chips/step3";
import { useFetchAllMenuData } from "@/hooks/use-fetch-all-menu-data";
import useAuth from "@/hooks/useAuth";
import CreateMenuSkeleton from "@/components/skeleton/create-menu-skeleton";
import StepFormButtons from "@/components/common/step-for-button";
import { getFirstErrorMessage } from "@/lib/utils";
import {
  createNewFishChips,
  clearFishChipsMessages,
  updateExistingFishChips,
} from "@/redux/slice/menu-manage-slice/fish-chips";
import { toast } from "react-hot-toast";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useStepFormHandler } from "@/hooks/useStepFormHandler";

export const fishChips_id = 8;

function CreateFishChips() {
  const location = useLocation();
  const { type } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const stepLabels = ["Step 1", "Step 2", "Step 3"];
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const item = location.state;

  const fetchOnly = useMemo(() => ["categories", "allergens", "addOns"], []);

  const { groupLoading } = useFetchAllMenuData(
    user.restaurant_id,
    fishChips_id,
    {
      fetchOnly,
    }
  );

  const { createLoading, error, updateLoading } = useSelector(
    (state) => state.menuManage.fishChips
  );

  const form = useForm({
    resolver: zodResolver(createFishChipsSchema),
    mode: "onChange",
    defaultValues: {
      item_no: "",
      name: "",
      description: "",
      category_id: null,
      allergen_ids: [],
      size: "",
      spice_level: null,
      vat: 0,
      status: "active",
      file_manager_id: undefined,
      add_on_ids: [],
      in_price: null,
      out_price: null,
      calorie: "",
    },
  });
  const errorMessage = getFirstErrorMessage(form);

  useEffect(() => {
    if (type !== "update") return;
    if (!item) {
      navigate("/backoffice/menu/fish-chips");
      return;
    }
    form.reset({
      type: "update",
      item_no: item.item_no || "",
      name: item.name || "",
      description: item.description || "",
      category_id: item.category_id || null,
      in_price: item.in_price || null,
      out_price: item.out_price || null,
      calorie: item.calorie ?? "",
      size: item.size || "",
      vat: item.vat ?? 0,
      spice_level: item.spice_level || null,
      status: item.status || "active",
      file_manager_id: item.file_manager_id || undefined,
      allergen_ids: item.allergens?.map((a) => a.id) || [],
    });

    if (item.file_manager_id && item.file_manager_url != null) {
      setFiles([
        {
          file_manager_id: item?.file_manager_id,
          preview: item?.file_manager_url,
          name: item?.name,
        },
      ]);
    }
  }, [type, item?.id, form]);

  const { step, nextStep, setStep, prevStep, entityId, loading } =
    useStepFormHandler({
      type,
      entity: "Fish and Chips",
      user,
      createAction: createNewFishChips,
      updateAction: updateExistingFishChips,
      totalSteps: stepLabels.length,
      item: item,
      toastSteps: { 0: false, 1: false, 2: true },
      successRedirectLink: "/backoffice/menu/fish-chips",
    });

  if (groupLoading) return <CreateMenuSkeleton />;

  return (
    <Layout2
      pageName={{
        title: `${
          type == "update" ? "Update Fish and chips" : "Add New Fish and chips"
        }`,
      }}
      paths={[
        { name: "home", path: "/home" },
        { name: "Back Office", path: "/backoffice/overview" },
        { name: "Menu Management", path: "/backoffice/menu/drinks" },
        { name: "Fish and chips", path: "/backoffice/menu/fish-chips" },
        {
          name: `${
            type == "update"
              ? "Update Fish and chips"
              : "Add New Fish and chips"
          }`,
          path: `/backoffice/menu/fish-chips/${type}`,
        },
      ]}
      LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
    >
      <div className="py-5 container mt-5">
        <Stepper
          steps={stepLabels}
          onStepChange={setStep}
          type={form.watch("type")}
          currentStep={step}
        />
        {/* {errorMessage && (
          <div className="text-cred mb-2 text-sm mt-4 flex justify-center items-center text-center">
            {errorMessage}
          </div>
        )} */}
        <Form {...form}>
          <form>
            {step === 0 && <Step1 fishChips_id={fishChips_id} form={form} />}
            {step === 1 && (
              <Step2
               item_id={entityId || item?.id}
                fishChips_id={fishChips_id}
                form={form}
              />
            )}
            {step === 2 && (
              <Step3 files={files} setFiles={setFiles} form={form} />
            )}

            <StepFormButtons
              step={step}
              totalSteps={stepLabels.length}
              isLoading={createLoading || updateLoading || loading}
              type={type}
              status={form.watch("status")}
              cancelLink="/backoffice/menu/fish-chips"
              onBack={prevStep}
              onNext={async () => {
                const data = form.getValues();
                await nextStep("draft", data, form); // Step 1 & Step 2 → draft
              }}
              onSubmit={async () => {
                const data = form.getValues();
                await nextStep(data?.status, data, form); // Step 3 → active
              }}
              onDraftSave={async () => {
                const data = form.getValues();
                await nextStep("draft", data, form, { draftButton: true });
              }}
            />
          </form>
        </Form>
      </div>
    </Layout2>
  );
}

export default CreateFishChips;

export const createFishChipsSchema = z.object({
  item_no: z
    .preprocess(
      (val) => {
        if (val === "" || val == null) return null;
        return Number(val);
      },
      z
        .number({
          invalid_type_error: "In price must be a number",
        })
        .nullable()
    )
    .optional(),
  in_price: z
    .preprocess(
      (val) => {
        if (val === "" || val == null) return null;
        return Number(val);
      },
      z
        .number({
          invalid_type_error: "Out price must be a number",
        })
        .nullable()
    )
    .optional(),
  out_price: z
    .preprocess(
      (val) => {
        if (val === "" || val == null) return null;
        return Number(val);
      },
      z
        .number({
          invalid_type_error: "Item no must be a number",
        })
        .nullable()
    )
    .optional(),

  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  category_id: z.preprocess((val) => {
    if (val === "" || val === undefined || val === null) return null;
    return Number(val);
  }, z.number().nullable().optional()),

  size: z.string().optional(),
  salad_ids: z.array(z.number()).optional(),
  sauce_ids: z.array(z.number()).optional(),
  allergen_ids: z.array(z.number()).optional(),
  spice_level: z
    .enum(["mild", "fairly_hot", "medium_hot", "very_hot", "extremely_hot"])
    .nullable()
    .default(null),
  vat: z
    .preprocess(
      (val) => (val === "" || val == null ? 0 : Number(val)),
      z.number({
        invalid_type_error: "VAT percentage must be a number",
      })
    )
    .refine((val) => val >= 0, {
      message: "VAT percentage cannot be negative",
    })
    .refine((val) => val <= 100, {
      message: "VAT percentage cannot exceed 100",
    })
    .optional(),
  calorie: z
    .preprocess(
      (val) => {
        if (val === "" || val == null) return 0;
        return Number(val);
      },
      z
        .number({
          invalid_type_error: "Calorie must be a number",
        })
        .nullable()
    )
    .optional(),
  file_manager_id: z
    .number({
      required_error: "Please upload an image",
      invalid_type_error: "File ID must be a number",
    })
    .int("File ID must be an integer")
    .optional(),
  status: z.enum(["active", "inactive", "draft"]).default("active"),
});
